/**
 * The realm map's ambient life (flag `realm_alive`): what moves on the
 * painting and how, as data (public/realm/ambient/<version>/ambient.json),
 * plus the pure maths the Pixi side runs on. Everything here is a function
 * of the ambient clock, so pausing is just stopping the clock, and the
 * tests need no browser.
 *
 * Positions are master pixels (the realm's 3504x2336 painting, or a world
 * map's own, public/world/<biome>/ambient/<version>). Each moving thing
 * is painted once, as parts, and moved in code: rocking, billowing,
 * flapping, undulating. Nothing is animated frame by frame.
 */

export type Vec = readonly [number, number];

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** How a part moves on its own, as a bent grid (a Pixi MeshPlane). */
export interface PartMotion {
  /** `billow`: a sail filling and easing, held along its top (the yard).
   *  `flutter`: a flag rippling, held along one side (the mast). */
  kind: 'billow' | 'flutter';
  /** Greatest movement, in master pixels. */
  amount: number;
  /** Seconds per cycle. */
  period: number;
  /** The held edge. */
  hold: 'top' | 'left' | 'right';
  /** Phase offset, in cycles, so neighbouring parts don't move in step. */
  phase?: number;
}

/** One painted part of a mover, placed relative to the mover's origin. */
export interface Part {
  frame: string;
  /** Top-left of the part, from the mover's origin, in master pixels. */
  x: number;
  y: number;
  motion?: PartMotion;
}

/** The foam and shade that sit a boat in the water. */
export interface Waterline {
  /** Foam ring frame; drawn squashed under the hull. */
  ring?: string;
  /** Its size and how far below the origin it sits. */
  w: number;
  h: number;
  dy: number;
  alpha: number;
}

interface ElementBase {
  id: string;
  /** Everywhere the element can reach, for running only what's on screen. */
  area: Rect;
  /** Below this zoom (screen pixels per master pixel) the element shows at
   *  rest and doesn't run: its movement is too small to see there, so the
   *  opening view of the whole realm doesn't pay for it. */
  minZoom?: number;
  /** On a world map, the slot (its node id) whose building it belongs to: it
   *  goes dark (lights, sparkles, butterflies, the dragon's breath, the
   *  horses) or grey (a creature) with the building while the slot is
   *  locked, and comes back with its colour. */
  slot?: number;
}

/** A colour matrix (Pixi's ColorMatrixFilter, 4 rows of 5) greying and
 *  dimming a picture as a world map's locked buildings are (the grey veil):
 *  `level` 0 leaves it as painted, 1 is fully locked. */
export function lockedMatrix(level: number): number[] {
  const k = Math.max(0, Math.min(1, level));
  const g = 0.85 * k;
  const dim = 1 - 0.4 * k;
  const lum = [0.299, 0.587, 0.114];
  const cool = [0.96, 0.98, 1.04];
  const out: number[] = [];
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) out.push(dim * ((r === c ? 1 - g : 0) + g * cool[r]! * lum[c]!));
    out.push(0, 0);
  }
  out.push(0, 0, 0, 1, 0);
  return out;
}

/** A mover that stays put and rocks: a moored ship. */
export interface RigElement extends ElementBase {
  kind: 'rig';
  /** The pivot it rocks about (its waterline), on the master. */
  at: Vec;
  parts: Part[];
  bob?: { px: number; period: number };
  rock?: { deg: number; period: number };
  waterline?: Waterline;
}

/** A mover that sails along a path and back. */
export interface PathElement extends ElementBase {
  kind: 'path';
  parts: Part[];
  /** The route, on the master. */
  path: Vec[];
  /** Master pixels per second. */
  speed: number;
  /** Seconds it waits at each end before turning. */
  pause: number;
  /** Which way the art faces (1 right, -1 left); it flips to face its way. */
  faces: 1 | -1;
  /** False for a mover that runs both ways without turning round (a mine
   *  cart on its rails). */
  turns?: boolean;
  /** A small jolt, master pixels, while it moves (a cart on its rails). */
  rattle?: number;
  bob?: { px: number; period: number };
  rock?: { deg: number; period: number };
  waterline?: Waterline;
}

/** Birds crossing now and then, flapping and gliding. */
export interface FlockElement extends ElementBase {
  kind: 'flock';
  /** Wing poses in flap order (up, level, down); the art faces right. */
  frames: string[];
  /** Each pose's pivot (the body), in the frame's own pixels, so the bird
   *  doesn't jump as its wings change. */
  pivots: Vec[];
  /** Seconds between birds (the mean); each bird's start is jittered. */
  every: number;
  /** Routes birds take, each start to end; a bird wanders a little off it. */
  lanes: { from: Vec; to: Vec }[];
  /** Master pixels per second, slowest and fastest. */
  speed: [number, number];
  /** Seconds per wingbeat. */
  flap: number;
  /** Share of the time spent gliding, wings level. */
  glide: number;
  /** Size, smallest and largest (1 = the art's size). */
  scale: [number, number];
  /** A faint shadow on the sea, this far below the bird. */
  shadow?: { dy: number; alpha: number };
  /** Birds flying together, fewest and most: each crossing is a small
   *  flock of this many, close together (doves). Without it, one at a time. */
  group?: [number, number];
}

/** The sea serpent: swims with its humps rolling along, dives with a
 *  splash and comes up again somewhere else. */
export interface SerpentElement extends ElementBase {
  kind: 'serpent';
  head: string;
  body: string;
  splash: string;
  ring: string;
  /** The head's pivot (the base of the neck, at the waterline), in the
   *  head frame's own pixels. */
  neck: Vec;
  /** Where it surfaces, in turn: the neck's base on the master, and which
   *  way it faces (1 right, -1 left). */
  spots: { at: Vec; faces: 1 | -1 }[];
  /** Body length behind the neck, and its thickness, in master pixels. */
  length: number;
  thickness: number;
  /** How high the humps rise, and how many show at once. */
  amplitude: number;
  humps: number;
  /** Seconds for a hump to roll the length of a hump. */
  wave: number;
  /** Master pixels per second it drifts forward while up. */
  swim: number;
  /** Seconds in each phase of a cycle. */
  cycle: { rise: number; swim: number; dive: number; under: number };
}

/** A lighthouse: a soft beam turning, and the lamp's glow. */
export interface BeamElement extends ElementBase {
  kind: 'beam';
  /** The lamp, on the master. */
  at: Vec;
  /** The beam's frame (its bright end on the left, middle height). */
  frame: string;
  length: number;
  width: number;
  /** Seconds per turn. */
  period: number;
  /** How much the turning circle is squashed, for the map's slant (0 to 1). */
  tilt: number;
  /** The beam is brightest pointing this way (radians, 0 = east, screen
   *  y down), over open sea, and fades when it points inland. */
  towards: number;
  /** How far either side of `towards` the beam shows (radians): it turns
   *  all the way round but lights only the sea side, fading out at the
   *  edges of this arc. Without it, it fades smoothly all the way round. */
  arc?: number;
  alpha: number;
  glow: { radius: number; alpha: number };
}

/** A cloud: drifting to and fro in place (the corners' clouds), or across
 *  the map and round again, with its shadow on the land or sea below. */
export interface CloudElement extends ElementBase {
  kind: 'cloud';
  frame: string;
  /** Where its middle rests (or starts, crossing), on the master. */
  at: Vec;
  /** Master pixels per frame pixel (the sky's sheet is stored small). */
  scale: number;
  /** Mirrored, so one painted cloud serves twice. */
  flip?: boolean;
  alpha?: number;
  /** To and fro about `at`: the furthest offset, seconds per cycle. */
  drift?: { dx: number; dy: number; period: number; phase?: number };
  /** Or from `at` to `to` and round again, master pixels per second;
   *  `offset` is the share of the trip done at time 0. */
  cross?: { to: Vec; speed: number; offset?: number };
  /** The shadow: how far below the cloud it falls, how dark, and its size
   *  against the cloud's. */
  shadow?: { dx: number; dy: number; alpha: number; scale?: number };
  /** Only the shadow shows: a cloud high above the viewer, its shadow
   *  passing over the land. */
  shadowOnly?: boolean;
}

/** Smoke or steam: soft puffs rising from a point, slowing, spreading and
 *  fading as they go, pushed by the wind. */
export interface PlumeElement extends ElementBase {
  kind: 'plume';
  /** Where puffs start (a chimney's mouth), on the master. */
  at: Vec;
  /** The puff frames, picked in turn by each puff's number. */
  frames: string[];
  /** Seconds between puffs, and how long each lasts. */
  every: number;
  life: number;
  /** Master pixels per second upward at the start (it slows as it
   *  rises), and the wind's push (master pixels per second). */
  rise: number;
  wind: Vec;
  /** How far apart, sideways, puffs start. */
  spread: number;
  /** A puff's width in master pixels: at its start, and at its end. */
  size: [number, number];
  /** Strongest opacity. */
  alpha: number;
  /** Colour multiplied in ('#rrggbb'): pink smoke from white puffs. */
  tint?: string;
}

/** A fountain's spray or a geyser: drops thrown up, falling back. */
export interface SprayElement extends ElementBase {
  kind: 'spray';
  /** The nozzle, on the master. */
  at: Vec;
  /** Seconds between drops. */
  every: number;
  /** Launch speed, master pixels per second (slowest, fastest), and the
   *  most a drop leans from straight up (radians). */
  speed: [number, number];
  angle: number;
  /** Master pixels per second squared. */
  gravity: number;
  /** A drop is gone once it falls this far below the nozzle (the bowl). */
  floor: number;
  /** A drop's length in master pixels (smallest, largest). */
  size: [number, number];
  alpha: number;
  /** A geyser's bursts: launch speed swells to full and falls back to
   *  `low` of it, every `period` seconds. */
  pulse?: { period: number; low: number };
  tint?: string;
}

/** One walker in a train, first to last. */
export interface Walker {
  frame: string;
  /** How far behind the walker before it it walks, master pixels along the
   *  route (0 for the first). */
  gap: number;
  /** Where it stands (its feet), in the frame's own pixels. */
  feet: Vec;
  /** Master pixels per frame pixel (default 1). */
  scale?: number;
  /** A rail car only (see {@link RailSpec}): how far along the picture's
   *  baseline, from its `feet`, its rear and its front wheel sets touch the
   *  rail, in frame pixels (negative behind the feet). The car is turned so
   *  both stand on the rail at every place along it. */
  bogies?: Vec;
}

/**
 * What makes a train a rail train: its cars ride a painted rail, one way,
 * and each is turned to put both wheel sets on it (a steam train on rails
 * that climb and fall). Walkers become cars: the `path` is the rail's top
 * line from the left edge to the right (the near rail, where the wheels
 * touch), each walker's `gap` is the distance between its middle and the
 * car in front's, `feet` is the middle of its wheels' bottom edge, and
 * `bogies` its wheel sets. The cars never mirror, and the train never turns
 * round: it enters beyond the left edge, runs to beyond the right, waits
 * `wait` (shortest, longest) seconds, and runs again. `speed` is its pace
 * in master pixels per second; `ramp`, `turn`, `gait`, `shadow` and `faces`
 * are not used.
 *
 * It is a layer drawn over the painting, never a ground piece: it takes no
 * taps (the ambient layer never does), and the keep-out and feet checks of
 * the ground pieces do not apply to it (worldAmbient.test.ts holds it to
 * its rail instead).
 */
export interface RailSpec {
  /** Where the lead car's middle stands, in master pixels along `path` from
   *  its start, when a trip begins and when it ends: every car is off the
   *  painting at both. */
  from: number;
  to: number;
  /** A car's small out-of-phase bob, master pixels up and its seconds. */
  bob: { px: number; period: number };
  /** The painted things the rails pass behind, where the train is hidden:
   *  the shape of each (master pixels), cut along the painted building, so
   *  the painting and its locked veil show through unchanged. `area` is the
   *  region the painting's check recorded for it. Nothing is drawn there:
   *  the train is only left out. */
  hides: RailHide[];
}

export interface RailHide {
  name: string;
  area: Rect;
  shape: Vec[];
  /** A plant or other prop painted over the rails rather than a building they pass behind. */
  prop?: boolean;
}

/** How a walker's legs move: drawn as one picture, the legs (the part below
 *  `legs`) swing to and fro, front against back, in step with the ground
 *  covered, so the feet don't slide. */
export interface Gait {
  /** Master pixels covered per leg cycle (two steps). */
  stride: number;
  /** How far the feet swing either way, master pixels. */
  swing: number;
  /** Where the legs begin, as a share of the frame's height from its top. */
  legs: number;
  /** How far the body rises and falls each step, master pixels. */
  bob: number;
}

/** Walkers in single file along a route and back: a camel caravan, a
 *  jousting knight on his horse. They wait at each end and turn round. */
export interface TrainElement extends ElementBase {
  kind: 'train';
  /** The route on the master, from the home end (where the first walker
   *  stands at rest) out; the others stand behind it along the route. */
  path: Vec[];
  /** Master pixels per second, once under way. */
  speed: number;
  /** Seconds to get up to speed, and to slow to a stop. */
  ramp: number;
  /** Seconds waited at the home end and at the far end. */
  wait: [number, number];
  /** Seconds a walker takes to turn round (at the end of a wait). */
  turn: number;
  /** Which way the art faces (1 right, -1 left). */
  faces: 1 | -1;
  walkers: Walker[];
  gait: Gait;
  /** A soft shadow under each walker: its size and darkness. */
  shadow?: { w: number; h: number; alpha: number };
  /** Seconds into its round trip at time 0. */
  offset?: number;
  /** Reduced motion shows the walkers this many seconds into their wait at
   *  home (default half way: still facing the way they came; the whole
   *  wait: turned to set off). On a loop, this many seconds round it. */
  rest?: number;
  /** A closed route (its last point is its first) walked round and round
   *  without stopping (horses trotting round a ring): each walker faces the
   *  way the route runs across the screen, turning where it bends round.
   *  `ramp`, `wait` and `turn` don't apply. */
  loop?: boolean;
  /** Set for a rail train (see {@link RailSpec}). */
  rail?: RailSpec;
}

/** Leaping now and then: a dolphin out of the sea and back in, a few
 *  times in a row, with a splash each time it breaks the surface. */
export interface LeapElement extends ElementBase {
  kind: 'leap';
  /** The leaper, level as at the top of a leap. */
  frame: string;
  /** Which way the art faces (1 right, -1 left). */
  faces: 1 | -1;
  splash: string;
  ring: string;
  /** Where a run of leaps starts (on the waterline), and which way it goes. */
  spots: { at: Vec; dir: 1 | -1 }[];
  /** Seconds between runs (the mean; each start is jittered). */
  every: number;
  /** Leaps in a run. */
  leaps: number;
  /** One leap: master pixels from going out to coming back in, how high
   *  it rises, and seconds in the air. */
  length: number;
  height: number;
  air: number;
  /** How far under the surface the leaper's middle starts and ends, so it
   *  comes out nose first. */
  sink: number;
  /** Master pixels per frame pixel (default 1). */
  scale?: number;
}

/** Lights that glow on the painting: flames flickering, lit windows
 *  glowing, a marquee's bulbs chasing round, neon twinkling. Soft glows
 *  added over the paint, so no art is needed. */
export interface LightsElement extends ElementBase {
  kind: 'lights';
  /** `flicker`: flames (torches, lanterns). `breathe`: lit windows, slowly
   *  brighter and softer, out of step. `chase`: bulbs lit in a wave that
   *  runs along them in order. `twinkle`: sparkles that flash now and then. */
  mode: 'flicker' | 'breathe' | 'chase' | 'twinkle';
  /** The glow's colour, '#rrggbb'. */
  colour: string;
  /** Each light: x and y on the master, and its glow's radius (a soft
   *  round glow drawn in code). */
  points?: [number, number, number][];
  /** Or glow pictures cut from the painting's own lights (windows in their
   *  shapes, a row of bulbs): each one a light, its top-left on the master
   *  and its size there per frame pixel. */
  sprites?: { frame: string; x: number; y: number; scale: number }[];
  /** Seconds per cycle: a breath, a lap of the chase, a twinkle. */
  period: number;
  /** Strongest opacity, and the weakest as a share of it. */
  alpha: number;
  low: number;
  /** A chase's bright groups running round at once (default 3). */
  groups?: number;
}

/** A river flowing: small strokes of light drifting downstream along its
 *  middle, each fading in and out over a short run. */
export interface FlowElement extends ElementBase {
  kind: 'flow';
  /** The river's middle on the master, upstream first. */
  path: Vec[];
  /** Its width at each point of the path, master pixels (or one for all). */
  width: number | number[];
  /** Master pixels per second downstream. */
  speed: number;
  /** River length per stroke, master pixels (how close they are). */
  every: number;
  /** How far a stroke drifts before it fades out, master pixels. */
  run: number;
  /** A stroke's length, master pixels (shortest, longest). */
  size: [number, number];
  alpha: number;
  /** Colour multiplied in ('#rrggbb'): cream strokes on the cocoa river. */
  tint?: string;
  /** Stretches (master pixels along the river, from and to) where it runs
   *  under something painted over it, a bridge: no strokes there. */
  hidden?: [number, number][];
}

/** A turn about a joint, for a piece that lives in place: a head dipping to
 *  graze, a tail swishing, an arm waving, a mermaid's tail swaying. The
 *  picture is drawn on a bent grid: the points inside `box` turn fully
 *  about `at`, fading out over `soft` pixels round the box, so nothing
 *  tears. */
export interface Joint {
  /** The pivot, in the frame's own pixels. */
  at: Vec;
  /** What turns: left, top, right and bottom in the frame's own pixels. */
  box: [number, number, number, number];
  /** How far past the box the turn fades out, frame pixels (default 6). */
  soft?: number;
  /** The greatest turn, degrees (positive turns clockwise on screen). */
  deg: number;
  /** `sway`: to and fro all the time (a mermaid's tail). `graze`: down for
   *  a while, nibbling, then up again for a while (a sheep's head).
   *  `burst`: a few quick swings now and then, still between (a tail
   *  swish). `wave`: now and then the arm goes up, waves to and fro a few
   *  times and comes down (a villager waving). */
  pattern: 'sway' | 'graze' | 'burst' | 'wave';
  /** Seconds per swing (sway), per graze and look up (graze), or from one
   *  burst to the next (burst). */
  period: number;
  /** Phase offset in cycles, so neighbours don't move in step. */
  phase?: number;
  /** A burst's swings, and seconds per swing (defaults 2 and 0.5). */
  swings?: number;
  swing?: number;
}

/** A character living in place: animals grazing, the fox at its den, the
 *  yeti waving, villagers, bathers in the warm pool. One clean picture per
 *  kind serves many, mirrored, scaled or tinted; the painted figure it
 *  stands for is painted out of the clean plate. */
export interface IdleElement extends ElementBase {
  kind: 'idle';
  frame: string;
  /** The picture's top-left on the master. */
  x: number;
  y: number;
  /** Master pixels per frame pixel (default 1). */
  scale?: number;
  /** Mirrored left to right (it then faces the other way). */
  flip?: boolean;
  /** Colour multiplied in ('#rrggbb'): a golden sheep. */
  tint?: string;
  /** A soft shadow on the ground: its middle, master pixels from the
   *  picture's top-left, its size and darkness. It stays put as the
   *  piece bobs. */
  shadow?: { x: number; y: number; w: number; h: number; alpha: number };
  joints?: Joint[];
  /** Breathing: the picture swells up from its foot by this share of its
   *  height, and back, every `period` seconds. */
  breathe?: { amount: number; period: number; phase?: number };
  /** Bobbing up and down (a bather in the water), master pixels. */
  bob?: { px: number; period: number; phase?: number };
  /** A second pose painted to match (the yeti with its arm up), the same
   *  size and place: every `period` seconds it fades in over the painting's
   *  own pose, holds for `hold` seconds with its own `joints` moving (a paw
   *  waving), and fades out. The piece's joints, breathing and bob move it
   *  too, so the two stay together. */
  pose?: {
    frame: string;
    period: number;
    hold: number;
    phase?: number;
    /** Seconds to fade in and out (default POSE_FADE): short, so the two
     *  poses are never seen together for more than a few frames. */
    fade?: number;
    joints?: Joint[];
  };
  /** More pictures painted to match, in order (a dragon breathing: its back
   *  and wings rising): over each `period` it eases from `frame` through
   *  them to the last and back, each fading into the next. */
  cycle?: { frames: string[]; period: number; phase?: number };
}

/** Butterflies fluttering over a meadow or a flower bed: each roams round
 *  its spot on its own looping course, wings beating. */
export interface FlitElement extends ElementBase {
  kind: 'flit';
  /** Butterflies seen from above, wings open, head up the picture. */
  frames: string[];
  /** Where they flutter: each spot's middle, and how far they roam from it
   *  across and up and down, master pixels. */
  spots: { at: Vec; r: Vec }[];
  /** Butterflies at each spot. */
  count: number;
  /** Master pixels per frame pixel. */
  scale: number;
  /** Wingbeats per second. */
  flap: number;
  /** Seconds for a loop of the roam (the mean). */
  period: number;
}

/** Little stars of light twinkling round a point and drifting up: a
 *  wizard's hands, a magic star. Drawn in code, added over the paint. */
export interface SparkleElement extends ElementBase {
  kind: 'sparkle';
  /** Where they appear: x and y on the master, and the radius round it. */
  points: [number, number, number][];
  /** Seconds between sparkles at each point (the mean). */
  every: number;
  /** Seconds a sparkle lasts. */
  life: number;
  /** Master pixels per second it drifts up. */
  rise: number;
  /** A sparkle's size, master pixels (smallest, largest). */
  size: Vec;
  colour: string;
  alpha: number;
}

/**
 * Trees, bushes and flowers swaying in the wind (the world maps): the
 * painting under a grey mask (white = sways; never a building, the road, a
 * plate or a still area) drawn shifted a few master pixels along the wind,
 * by a breeze whose gusts roll across the painting, so neighbouring trees
 * move out of step. Like the sea's ripple it moves a copy of the painting,
 * so it needs no art beyond the mask.
 */
export interface SwayElement extends ElementBase {
  kind: 'sway';
  /** The mask image, relative to the spec, and where it sits on the master. */
  mask: string;
  rect: Rect;
  /** The greatest shift, master pixels (a few at most). */
  amount: number;
  /** Seconds per sway. */
  period: number;
  /** Master pixels from one gust's crest to the next, across the painting. */
  wavelength: number;
  /** The way the wind blows (made a unit vector); the gusts roll along it. */
  wind: Vec;
  /** The copy's resolution, as a share of the screen's (default 1). */
  detail?: number;
}

export type AmbientElement =
  | SwayElement
  | RigElement
  | PathElement
  | FlockElement
  | SerpentElement
  | BeamElement
  | CloudElement
  | PlumeElement
  | SprayElement
  | TrainElement
  | LeapElement
  | LightsElement
  | FlowElement
  | IdleElement
  | FlitElement
  | SparkleElement;

/** A stretch of sea that ripples, under a grey mask (white = moves). */
export interface WaterSpec {
  id: string;
  /** The mask image, relative to the spec. */
  mask: string;
  /** Where the mask sits on the master. */
  rect: Rect;
  /** How far the water shifts, in master pixels. */
  strength: number;
  /** Master pixels per repeat of the ripple pattern. */
  size: number;
  /** Pattern repeats per second the water drifts. */
  speed: number;
  /** Sun glints on the ripples (0 for none). */
  glint: number;
  /** The moving sea's resolution, as a share of the screen's (default 1):
   *  lower is cheaper on phones, and softer. */
  detail?: number;
}

export interface AmbientSpec {
  version: 1;
  /** The pieces' texture sheets (Pixi spritesheets), relative to the spec:
   *  the movers, and the sky's soft shapes (clouds, puffs) stored small. */
  sheets: string[];
  water: WaterSpec[];
  elements: AmbientElement[];
}

const KINDS = new Set([
  'rig',
  'path',
  'flock',
  'serpent',
  'beam',
  'cloud',
  'plume',
  'spray',
  'train',
  'leap',
  'lights',
  'flow',
  'idle',
  'flit',
  'sparkle',
  'sway',
]);

/** The spec from its JSON, or an error naming what's wrong. Unknown element
 *  kinds are dropped, so an older build ignores a newer kind of mover. */
export function parseAmbient(raw: unknown): AmbientSpec {
  if (!raw || typeof raw !== 'object') throw new Error('ambient: not an object');
  const r = raw as Partial<AmbientSpec> & { sheet?: unknown };
  if (r.version !== 1) throw new Error('ambient: unknown version');
  const sheets = Array.isArray(r.sheets)
    ? r.sheets.filter((s): s is string => typeof s === 'string')
    : typeof r.sheet === 'string'
      ? [r.sheet]
      : [];
  if (sheets.length === 0) throw new Error('ambient: no sheet');
  const elements = Array.isArray(r.elements) ? r.elements : [];
  for (const e of elements) {
    if (!e || typeof e.id !== 'string' || !isRect(e.area))
      throw new Error(`ambient: element ${String(e?.id)} needs an id and an area`);
    if (e.kind === 'sway' && (typeof e.mask !== 'string' || !isRect(e.rect)))
      throw new Error(`ambient: sway ${e.id} needs a mask and a rect`);
  }
  const water = Array.isArray(r.water) ? r.water : [];
  for (const w of water) {
    if (typeof w.mask !== 'string' || !isRect(w.rect))
      throw new Error(`ambient: water ${String(w?.id)} needs a mask and a rect`);
  }
  return {
    version: 1,
    sheets,
    water,
    elements: elements.filter((e) => KINDS.has(e.kind)),
  };
}

function isRect(r: unknown): r is Rect {
  const q = r as Rect;
  return !!q && [q.x, q.y, q.w, q.h].every((n) => typeof n === 'number' && Number.isFinite(n));
}

/** True when two rectangles overlap. */
export function overlaps(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** A rectangle grown by `m` on every side. */
export function grow(r: Rect, m: number): Rect {
  return { x: r.x - m, y: r.y - m, w: r.w + 2 * m, h: r.h + 2 * m };
}

/** A smooth wave from -1 to 1. */
export function wave(t: number, period: number, phase = 0): number {
  return Math.sin(2 * Math.PI * (t / period + phase));
}

// ---- paths ----

/** A path's length in master pixels. */
export function pathLength(path: readonly Vec[]): number {
  let d = 0;
  for (let i = 1; i < path.length; i++) {
    d += Math.hypot(path[i]![0] - path[i - 1]![0], path[i]![1] - path[i - 1]![1]);
  }
  return d;
}

/** The point `d` master pixels along a path, clamped to its ends. */
export function pointAlong(path: readonly Vec[], d: number): { x: number; y: number } {
  if (path.length === 0) return { x: 0, y: 0 };
  let left = Math.max(0, d);
  for (let i = 1; i < path.length; i++) {
    const [ax, ay] = path[i - 1]!;
    const [bx, by] = path[i]!;
    const seg = Math.hypot(bx - ax, by - ay);
    if (left <= seg && seg > 0) {
      const f = left / seg;
      return { x: ax + (bx - ax) * f, y: ay + (by - ay) * f };
    }
    left -= seg;
  }
  const [x, y] = path[path.length - 1]!;
  return { x, y };
}

/**
 * Where a path follower is at time `t`: out along the path, a pause, back,
 * a pause, and round again. Eased at the ends, so it slows to turn.
 * `facing` is the way it faces, 1 out and -1 back, swinging smoothly from
 * one to the other through each pause (it turns round while it waits).
 * `moving` is false while it waits.
 */
export function followPath(
  path: readonly Vec[],
  speed: number,
  pause: number,
  t: number,
): { x: number; y: number; facing: number; moving: boolean } {
  const len = pathLength(path);
  const leg = len / Math.max(1e-6, speed);
  const lap = 2 * (leg + pause);
  const u = ((t % lap) + lap) % lap;
  const ease = (f: number): number => f * f * (3 - 2 * f);
  const swing = (f: number): number => Math.cos(Math.PI * ease(f));
  if (u < leg) return { ...pointAlong(path, len * ease(u / leg)), facing: 1, moving: true };
  if (u < leg + pause) {
    return { ...pointAlong(path, len), facing: swing((u - leg) / pause), moving: false };
  }
  if (u < 2 * leg + pause) {
    const at = pointAlong(path, len * (1 - ease((u - leg - pause) / leg)));
    return { ...at, facing: -1, moving: true };
  }
  return {
    ...pointAlong(path, 0),
    facing: -swing((u - 2 * leg - pause) / pause),
    moving: false,
  };
}

/** A point along a path, and the way the path runs there (a unit vector).
 *  Past either end it carries straight on, so a walker can stand just
 *  beyond the end of its route. */
export function alongWithTangent(
  path: readonly Vec[],
  d: number,
): { x: number; y: number; tx: number; ty: number } {
  if (path.length < 2) {
    const [x, y] = path[0] ?? [0, 0];
    return { x, y, tx: 1, ty: 0 };
  }
  let left = d;
  for (let i = 1; i < path.length; i++) {
    const [ax, ay] = path[i - 1]!;
    const [bx, by] = path[i]!;
    const seg = Math.hypot(bx - ax, by - ay);
    const last = i === path.length - 1;
    if ((left <= seg || last) && seg > 0) {
      const tx = (bx - ax) / seg;
      const ty = (by - ay) / seg;
      // Before the start (only on the first segment) or past the end (the
      // last), the straight line carries on.
      const f = i === 1 ? left : Math.max(0, left);
      return { x: ax + tx * f, y: ay + ty * f, tx, ty };
    }
    left -= seg;
  }
  const [x, y] = path[path.length - 1]!;
  return { x, y, tx: 1, ty: 0 };
}

// ---- walkers ----

/** One walker of a train at a moment. */
export interface WalkerState {
  x: number;
  y: number;
  /** 1 going out (away from the home end), -1 coming back, and between
   *  while it turns round. */
  facing: number;
  /** Its legs' cycle, in cycles (only the fraction matters). */
  step: number;
  /** Its speed as a share of full: 0 standing, 1 under way. */
  pace: number;
}

/** A train's timings: how far its lead walks (so the last walker ends at
 *  the route's end), its ramp and one leg in seconds, and the round trip. */
export function trainLap(e: Pick<TrainElement, 'path' | 'speed' | 'ramp' | 'wait' | 'walkers'>): {
  reach: number;
  ramp: number;
  leg: number;
  lap: number;
} {
  const gaps = e.walkers.reduce((a, w, i) => a + (i ? w.gap : 0), 0);
  const reach = Math.max(0, pathLength(e.path) - gaps);
  const v = Math.max(1e-6, e.speed);
  const ramp = Math.max(0, Math.min(e.ramp, reach / v));
  const leg = reach / v + ramp;
  return { reach, ramp, leg, lap: 2 * leg + e.wait[0] + e.wait[1] };
}

/**
 * Where a train's lead is along its route at time `t` (master pixels from
 * the home end), how fast it's going (a share of full), and which way it
 * faces. Out along the route, a wait, back, a wait: getting up to speed
 * and slowing to a stop over `ramp` seconds, turning round at the end of
 * each wait.
 */
export function trainLead(
  e: Pick<TrainElement, 'path' | 'speed' | 'ramp' | 'wait' | 'turn' | 'walkers' | 'offset'>,
  t: number,
): { d: number; pace: number; facing: number; reach: number } {
  const { reach, ramp, leg, lap } = trainLap(e);
  const v = Math.max(1e-6, e.speed);
  const [waitHome, waitFar] = e.wait;
  if (lap <= 0) return { d: 0, pace: 0, facing: 1, reach };
  const u = (((t + (e.offset ?? 0)) % lap) + lap) % lap;
  /** Distance covered `s` seconds into a leg, and the pace there. */
  const run = (s: number): { d: number; pace: number } => {
    if (ramp <= 0) return { d: Math.min(reach, v * s), pace: 1 };
    const a = v / ramp;
    if (s < ramp) return { d: 0.5 * a * s * s, pace: s / ramp };
    if (s < leg - ramp) return { d: 0.5 * v * ramp + v * (s - ramp), pace: 1 };
    const r = Math.max(0, leg - s);
    return { d: reach - 0.5 * a * r * r, pace: r / ramp };
  };
  /** Turning round at the end of a wait, just before setting off again:
   *  from `from` to -`from`. */
  const turning = (s: number, wait: number, from: number): number => {
    const turn = Math.min(e.turn, wait);
    const f = Math.min(1, Math.max(0, (s - (wait - turn) * 0.9) / Math.max(1e-6, turn)));
    return from * Math.cos(Math.PI * f * f * (3 - 2 * f));
  };
  if (u < leg) return { ...run(u), facing: 1, reach };
  if (u < leg + waitFar) return { d: reach, pace: 0, facing: turning(u - leg, waitFar, 1), reach };
  if (u < 2 * leg + waitFar) {
    const r = run(u - leg - waitFar);
    return { d: reach - r.d, pace: r.pace, facing: -1, reach };
  }
  return { d: 0, pace: 0, facing: turning(u - 2 * leg - waitFar, waitHome, -1), reach };
}

/** A looping walker turns round through a bend, while less than this share
 *  of each step goes across the screen. */
const STEER = 0.35;

/** A looping train's walkers at time `t`: round and round at full pace,
 *  each its gap further round, facing the way the route runs across. */
function loopWalkersAt(e: TrainElement, t: number): WalkerState[] {
  const len = pathLength(e.path);
  if (len <= 0) return [];
  const lead = (t + (e.offset ?? 0)) * e.speed;
  const out: WalkerState[] = [];
  let behind = 0;
  e.walkers.forEach((w, i) => {
    if (i) behind += w.gap;
    const d = lead + behind;
    const at = pointAlong(e.path, ((d % len) + len) % len);
    // The way the route runs, over a short stretch, so a corner turns it
    // smoothly rather than at once.
    const a = pointAlong(e.path, (((d - 12) % len) + len) % len);
    const b = pointAlong(e.path, (((d + 12) % len) + len) % len);
    const run = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const across = (b.x - a.x) / run;
    out.push({
      x: at.x,
      y: at.y,
      facing: Math.max(-1, Math.min(1, across / STEER)),
      step: d / Math.max(1e-6, e.gait.stride) + i * 0.37,
      pace: 1,
    });
  });
  return out;
}

/** Every walker of a train at time `t`: the first at the lead's place, the
 *  others behind it along the route by their gaps. */
export function walkersAt(e: TrainElement, t: number): WalkerState[] {
  if (e.loop) return loopWalkersAt(e, t);
  const lead = trainLead(e, t);
  const out: WalkerState[] = [];
  let behind = 0;
  e.walkers.forEach((w, i) => {
    if (i) behind += w.gap;
    const d = lead.d + behind;
    const at = alongWithTangent(e.path, d);
    out.push({
      x: at.x,
      y: at.y,
      facing: lead.facing,
      // Legs cycle with the ground covered; each walker out of step.
      step: d / Math.max(1e-6, e.gait.stride) + i * 0.37,
      pace: lead.pace,
    });
  });
  return out;
}

// ---- rail trains ----

/** The seconds a rail train takes for one trip, from where it starts to
 *  where it ends. */
export function railTripSeconds(e: Pick<TrainElement, 'speed'> & { rail: Pick<RailSpec, 'from' | 'to'> }): number {
  return (e.rail.to - e.rail.from) / Math.max(1e-6, e.speed);
}

/** How long a rail train waits after trip `n` (shortest and longest are
 *  `wait`; each wait is picked from the trip's number, so the same time
 *  always gives the same train). */
export function railWait(e: Pick<TrainElement, 'wait'>, n: number): number {
  const [lo, hi] = e.wait;
  return lo + (Math.max(lo, hi) - lo) * hash01(n * 13 + 5);
}

/** Where a rail train's lead car is at time `t`: its middle's distance along
 *  the rail (master pixels from its start, negative before the rail), or
 *  null while the train waits off the painting. `trip` counts the trips. */
export function railLead(
  e: Pick<TrainElement, 'speed' | 'wait' | 'offset'> & { rail: Pick<RailSpec, 'from' | 'to'> },
  t: number,
): { s: number; trip: number } | null {
  const run = railTripSeconds(e);
  let u = Math.max(0, t + (e.offset ?? 0));
  for (let n = 0; n < 100_000; n++) {
    if (u < run) return { s: e.rail.from + e.speed * u, trip: n };
    u -= run;
    const wait = railWait(e, n);
    if (u < wait) return null;
    u -= wait;
  }
  return null;
}

/** A rail car, placed. */
export interface RailCar {
  /** Where its `feet` (the middle of its wheels' bottom edge) stand, master pixels. */
  x: number;
  y: number;
  /** Radians, clockwise on screen as Pixi turns (the way the rail runs). */
  rotation: number;
  /** Where its two wheel sets touch, rear then front. */
  wheels: [Vec, Vec];
}

/**
 * One car with its middle `s` along the rail: its wheel sets (`bogies`, the
 * picture's pixels from its feet, rear then front) stand on the rail's points
 * A and B, the car is turned to the way A to B runs, and its feet put on
 * that line so that it is as far from A as the rear wheels are behind the
 * feet, scaled by how much shorter the chord is than the wheelbase. Both
 * wheel sets stand on the rail at every place along it, however it climbs
 * and falls. (The painting's `train_path.json` note, as written.)
 */
export function placeCar(path: readonly Vec[], s: number, bogies: Vec): RailCar {
  const [rear, front] = bogies;
  const a = alongWithTangent(path, s + rear);
  const b = alongWithTangent(path, s + front);
  const chord = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  const rotation = Math.atan2(b.y - a.y, b.x - a.x);
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const ratio = chord / Math.max(1e-6, front - rear);
  const x = a.x + cos * -rear * ratio;
  const y = a.y + sin * -rear * ratio;
  return {
    x,
    y,
    rotation,
    wheels: [
      [x + cos * rear, y + sin * rear],
      [x + cos * front, y + sin * front],
    ],
  };
}

/** Every car of a rail train at time `t`, lead first (nothing while the train
 *  waits), each with its small bob out of step with the others. The bob
 *  lifts the car, so its wheels leave the rail by at most its `px`. */
export function railCarsAt(e: TrainElement & { rail: RailSpec }, t: number): RailCar[] {
  const lead = railLead(e, t);
  if (!lead) return [];
  let behind = 0;
  return e.walkers.map((w, i) => {
    if (i) behind += w.gap;
    const car = placeCar(e.path, lead.s - behind, w.bogies ?? [0, 0]);
    const lift = e.rail.bob.px * 0.5 * (1 + Math.sin(2 * Math.PI * (t / e.rail.bob.period + i * 0.37)));
    const [rear, front] = w.bogies ?? [0, 0];
    const cos = Math.cos(car.rotation);
    const sin = Math.sin(car.rotation);
    const x = car.x;
    const y = car.y - lift;
    return {
      x,
      y,
      rotation: car.rotation,
      wheels: [
        [x + cos * rear, y + sin * rear],
        [x + cos * front, y + sin * front],
      ],
    };
  });
}

/**
 * How far a walker's legs swing at a point down its height (`v`, 0 at the
 * top, 1 at the feet) and along it (`u`, 0 at the front, 1 at the back),
 * as a share of the gait's swing: nothing above the legs, most at the
 * feet; the front legs one way and the back legs the other, in turn.
 */
export function legSwing(gait: Pick<Gait, 'legs'>, step: number, u: number, v: number): number {
  const below = Math.max(0, (v - gait.legs) / Math.max(1e-6, 1 - gait.legs));
  return below * Math.cos(Math.PI * u) * Math.sin(2 * Math.PI * step);
}

// ---- leaping ----

/** A leaper in the air, or the splashes it leaves. */
export interface Leap {
  /** Which run and which leap in it (stable while it lasts). */
  n: number;
  /** Seconds since it left the water. */
  age: number;
  /** In the air: its middle, its turn (radians, along its flight) and which
   *  way it goes; null once it's back in. */
  body: { x: number; y: number; rotation: number } | null;
  dir: 1 | -1;
  /** The waterline it leaves and enters. */
  water: number;
  /** Where it breaks the surface going out and coming back in. */
  out: Vec;
  in: Vec;
}

/** Seconds a splash lasts. */
export const SPLASH_SECONDS = 1.1;

/**
 * The leaps under way at time `t`. Run n starts at about n * every seconds
 * (jittered) at a spot picked for it; its leaps follow one another along
 * the way it goes. The same time always gives the same leaps.
 */
export function leapsAt(e: LeapElement, t: number): Leap[] {
  const out: Leap[] = [];
  if (e.spots.length === 0 || e.every <= 0 || e.leaps <= 0 || e.air <= 0) return out;
  const between = e.air + 0.45;
  const span = (e.leaps - 1) * between + e.air + SPLASH_SECONDS;
  const first = Math.max(0, Math.floor((t - span) / e.every) - 1);
  const last = Math.floor(t / e.every) + 1;
  for (let n = first; n <= last; n++) {
    const start = (n + 0.7 * (hash01(n * 11 + 1) - 0.5)) * e.every;
    if (start > t || t - start > span) continue;
    const spot = e.spots[Math.floor(hash01(n * 11 + 2) * e.spots.length)]!;
    for (let j = 0; j < e.leaps; j++) {
      const age = t - start - j * between;
      if (age < 0 || age > e.air + SPLASH_SECONDS) continue;
      const at: Vec = [spot.at[0] + spot.dir * j * e.length * 1.3, spot.at[1]];
      out.push(leapAt(e, n * 16 + j, at, spot.dir, age));
    }
  }
  return out;
}

/** The share of a leap's time in the air when its middle crosses the
 *  surface going out (and, from the end, coming back in). */
export function leapCrossing(e: Pick<LeapElement, 'height' | 'sink'>): number {
  const rise = e.height + e.sink;
  return 0.5 * (1 - Math.sqrt(Math.max(0, 1 - e.sink / Math.max(1e-6, rise))));
}

/** One leap from `at` (on the waterline) going `dir`, `age` seconds after
 *  it left: its middle's flight starts `sink` under the surface, rises and
 *  falls back as far, so the nose breaks the surface first. */
export function leapAt(
  e: Pick<LeapElement, 'length' | 'height' | 'sink' | 'air'>,
  n: number,
  at: Vec,
  dir: 1 | -1,
  age: number,
): Leap {
  const [x0, y0] = at;
  const k = age / e.air;
  const rise = e.height + e.sink;
  let body: Leap['body'] = null;
  if (k <= 1) {
    const vx = dir * e.length;
    const vy = -rise * 4 * (1 - 2 * k);
    body = {
      x: x0 + dir * e.length * k,
      y: y0 + e.sink - rise * 4 * k * (1 - k),
      // Along its flight, with the art flipped to face its way.
      rotation: Math.atan2(vy * dir, vx * dir),
    };
  }
  const c = leapCrossing(e);
  return {
    n,
    age,
    body,
    dir,
    water: y0,
    out: [x0 + dir * e.length * c, y0],
    in: [x0 + dir * e.length * (1 - c), y0],
  };
}

// ---- lights ----

/**
 * How bright light `i` of `count` is at time `t` (0 to 1, from `low` up):
 * a flame's flicker, a window's slow breath, a marquee's chase, or a
 * sparkle's twinkle. Each light has its own phase, so none move in step
 * unless they should (the chase).
 */
export function lightLevel(
  e: Pick<LightsElement, 'mode' | 'period' | 'low' | 'groups'>,
  t: number,
  i: number,
  count: number,
): number {
  const p = Math.max(1e-3, e.period);
  const h = hash01(i * 29 + 7);
  let f: number;
  if (e.mode === 'flicker') {
    // Three sines at odd rates: restless, never repeating soon.
    const a = Math.sin(2 * Math.PI * (t / p) * 1.0 + h * 9);
    const b = Math.sin(2 * Math.PI * (t / p) * 2.71 + h * 23);
    const c = Math.sin(2 * Math.PI * (t / p) * 6.13 + h * 41);
    f = 0.5 + 0.22 * a + 0.16 * b + 0.12 * c;
  } else if (e.mode === 'breathe') {
    f = 0.5 + 0.5 * Math.sin(2 * Math.PI * (t / (p * (0.8 + 0.4 * h)) + h));
  } else if (e.mode === 'chase') {
    const groups = e.groups ?? 3;
    const w = Math.cos(2 * Math.PI * (t / p - (i / Math.max(1, count)) * groups));
    f = Math.max(0, w) ** 4;
  } else {
    // Twinkle: a quick flash once a cycle, at its own moment.
    const u = t / (p * (0.7 + 0.6 * h)) + h;
    const k = u - Math.floor(u);
    f = k < 0.25 ? Math.sin((Math.PI * k) / 0.25) ** 2 : 0;
  }
  return e.low + (1 - e.low) * Math.min(1, Math.max(0, f));
}

// ---- rivers ----

/** One stroke of light on a river. */
export interface Streak {
  n: number;
  x: number;
  y: number;
  /** Along the river, radians. */
  rotation: number;
  /** Length, master pixels. */
  size: number;
  alpha: number;
}

/** A river's width at distance `d` along it. */
function widthAt(e: Pick<FlowElement, 'path' | 'width'>, d: number): number {
  if (typeof e.width === 'number') return e.width;
  const ws = e.width;
  let left = d;
  for (let i = 1; i < e.path.length; i++) {
    const seg = Math.hypot(e.path[i]![0] - e.path[i - 1]![0], e.path[i]![1] - e.path[i - 1]![1]);
    if (left <= seg && seg > 0) {
      const f = left / seg;
      return (ws[i - 1] ?? 0) * (1 - f) + (ws[i] ?? ws[i - 1] ?? 0) * f;
    }
    left -= seg;
  }
  return ws[ws.length - 1] ?? 0;
}

/**
 * The strokes on a river at time `t`. Stroke n has its own stretch of the
 * river; each time round it starts at a fresh spot in that stretch and a
 * fresh place across the river, drifts `run` pixels downstream, fading in
 * and out. The same time always gives the same strokes.
 */
export function streaksAt(e: FlowElement, t: number): Streak[] {
  const out: Streak[] = [];
  const len = pathLength(e.path);
  if (len <= 0 || e.every <= 0 || e.run <= 0) return out;
  const count = Math.floor(len / e.every);
  for (let n = 0; n < count; n++) {
    const u = (t * e.speed) / e.run + hash01(n * 31 + 1);
    const c = Math.floor(u);
    const k = u - c;
    const d = (n + 0.9 * hash01(n * 31 + c * 977 + 2)) * e.every + k * e.run;
    if (d > len) continue;
    const half = e.size[1] / 2;
    if (e.hidden?.some(([a, b]) => d > a - half && d < b + half)) continue;
    const at = alongWithTangent(e.path, d);
    const across = (hash01(n * 31 + c * 613 + 3) - 0.5) * 0.7 * widthAt(e, d);
    out.push({
      n,
      x: at.x - at.ty * across,
      y: at.y + at.tx * across,
      rotation: Math.atan2(at.ty, at.tx),
      size: e.size[0] + (e.size[1] - e.size[0]) * hash01(n * 31 + c * 211 + 4),
      alpha: e.alpha * Math.sin(Math.PI * k) ** 2,
    });
  }
  return out;
}

// ---- birds ----

/** A repeatable random number in [0, 1) for a seed. */
export function hash01(seed: number): number {
  let h = Math.imul(seed ^ 0x9e3779b9, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** One bird in flight. */
export interface Bird {
  /** Which bird it is (stable while it flies). */
  n: number;
  x: number;
  y: number;
  /** 1 flying right, -1 left. */
  dir: 1 | -1;
  /** Index into the flock's frames. */
  frame: number;
  scale: number;
}

/** Master pixels between birds of a group, one behind the next. */
export const GROUP_GAP = 34;
/** The most birds in a group. */
const GROUP_MAX = 8;

/**
 * The birds in the air at time `t`. Crossing n sets off at about n * every
 * seconds (jittered), on a lane picked for it, at its own speed and size,
 * so the same time always gives the same birds. With `group`, each crossing
 * is a small flock: its birds trail the first, a little to either side,
 * each with its own wingbeat.
 */
export function birdsAt(f: FlockElement, t: number): Bird[] {
  const out: Bird[] = [];
  if (f.lanes.length === 0 || f.every <= 0) return out;
  const longest = Math.max(
    ...f.lanes.map((l) => Math.hypot(l.to[0] - l.from[0], l.to[1] - l.from[1])),
  );
  const slowest = Math.max(1e-6, f.speed[0]);
  const [few, most] = f.group ?? [1, 1];
  const trail = (Math.min(GROUP_MAX, most) - 1) * GROUP_GAP * 1.2;
  const first = Math.floor((t - (longest + trail) / slowest) / f.every) - 1;
  const last = Math.floor(t / f.every) + 1;
  for (let n = Math.max(0, first); n <= last; n++) {
    const start = (n + 0.8 * (hash01(n * 7 + 1) - 0.5)) * f.every;
    if (start > t) continue;
    const lane = f.lanes[Math.floor(hash01(n * 7 + 2) * f.lanes.length)]!;
    const speed = f.speed[0] + (f.speed[1] - f.speed[0]) * hash01(n * 7 + 3);
    const back = hash01(n * 7 + 4) < 0.5;
    const [ax, ay] = back ? lane.to : lane.from;
    const [bx, by] = back ? lane.from : lane.to;
    const dist = Math.hypot(bx - ax, by - ay);
    const along = (t - start) * speed;
    // Wander off the lane: a slow sway across it, and a lane offset.
    const nx = -(by - ay) / dist;
    const ny = (bx - ax) / dist;
    const lift = 40 * (hash01(n * 7 + 5) - 0.5);
    const scale = f.scale[0] + (f.scale[1] - f.scale[0]) * hash01(n * 7 + 6);
    const count = Math.min(GROUP_MAX, few + Math.floor(hash01(n * 7 + 8) * (most - few + 1)));
    for (let m = 0; m < count; m++) {
      const h = (k: number): number => hash01(n * 97 + m * 13 + k);
      // Each bird of a group a little behind the one before, to one side.
      const at = along - m * GROUP_GAP * (0.8 + 0.4 * h(1));
      if (at > dist || at < 0) continue;
      const side = m === 0 ? 0 : (m % 2 ? 1 : -1) * (10 + 9 * m) + 16 * (h(2) - 0.5);
      const k = at / dist;
      const off = lift + side + 14 * Math.sin(at / 90 + n + m * 1.7);
      out.push({
        n: n * GROUP_MAX + m,
        x: ax + (bx - ax) * k + nx * off,
        y: ay + (by - ay) * k + ny * off,
        dir: bx >= ax ? 1 : -1,
        frame: wingFrame(f, t + n * 0.37 + m * 0.23),
        scale: scale * (m === 0 ? 1 : 0.9 + 0.12 * h(3)),
      });
    }
  }
  return out;
}

/** The wing pose at time `t`: beats of up, level, down, level, with glides
 *  (wings level) between runs of beats. */
export function wingFrame(f: Pick<FlockElement, 'frames' | 'flap' | 'glide'>, t: number): number {
  const level = Math.min(1, f.frames.length - 1);
  const run = f.flap * 4;
  const cycle = run / Math.max(0.05, 1 - f.glide);
  const u = ((t % cycle) + cycle) % cycle;
  if (u >= run) return level;
  const beat = Math.floor((u % f.flap) / (f.flap / 4));
  const order = [0, level, f.frames.length - 1, level];
  return order[beat] ?? level;
}

// ---- the serpent ----

export type SerpentPhase = 'rise' | 'swim' | 'dive' | 'under';

export interface SerpentState {
  /** Which spot it is at (index into spots). */
  spot: number;
  phase: SerpentPhase;
  /** How far up it is: 0 under, 1 fully up. */
  up: number;
  /** Seconds into this surfacing, for the drift forward. */
  since: number;
  /** Seconds since it last broke the surface (going down or coming up), for
   *  the splash; Infinity when there's no splash to show. */
  splash: number;
}

/** Where the serpent is in its cycle at time `t`. */
export function serpentAt(s: SerpentElement, t: number): SerpentState {
  const { rise, swim, dive, under } = s.cycle;
  const total = rise + swim + dive + under;
  const k = Math.floor(t / total);
  const u = t - k * total;
  const spot = ((k % s.spots.length) + s.spots.length) % s.spots.length;
  const ease = (f: number): number => f * f * (3 - 2 * f);
  if (u < rise) return { spot, phase: 'rise', up: ease(u / rise), since: u, splash: u };
  if (u < rise + swim) return { spot, phase: 'swim', up: 1, since: u, splash: u };
  if (u < rise + swim + dive) {
    const f = (u - rise - swim) / dive;
    return { spot, phase: 'dive', up: 1 - ease(f), since: u, splash: Infinity };
  }
  // Under: the splash of going down plays at the start of this phase.
  return { spot, phase: 'under', up: 0, since: u, splash: u - rise - swim - dive };
}

/**
 * The serpent's body as points from the neck (s = 0) back to the tail
 * (s = length): x behind the neck (negative), and h, how high its middle
 * is above the water (negative is under). A wave
 * runs back along the body, so humps roll from head to tail; it tapers
 * into the water at both ends.
 */
export function serpentBody(
  s: Pick<SerpentElement, 'length' | 'amplitude' | 'humps' | 'wave'>,
  t: number,
  up: number,
  points = 32,
): { x: number; h: number }[] {
  const out: { x: number; h: number }[] = [];
  const k = (Math.PI * 2 * s.humps) / s.length;
  for (let i = 0; i < points; i++) {
    const d = (i / (points - 1)) * s.length;
    // Ends dip under: the neck end rises out of the water to the neck.
    const taper = Math.min(1, d / (s.length * 0.12), (s.length - d) / (s.length * 0.2));
    const h = Math.sin(k * d - (Math.PI * 2 * t) / s.wave);
    out.push({ x: -d, h: s.amplitude * up * Math.max(0, taper) * h });
  }
  return out;
}

/** Where the body breaks the surface (master pixels behind the neck),
 *  for the foam rings. */
export function surfaceCrossings(
  s: Pick<SerpentElement, 'length' | 'humps' | 'wave'>,
  t: number,
): number[] {
  const k = (Math.PI * 2 * s.humps) / s.length;
  const shift = (Math.PI * 2 * t) / s.wave;
  const out: number[] = [];
  // sin(k d - shift) = 0 where k d - shift = n pi.
  const n0 = Math.ceil(-shift / Math.PI);
  for (let n = n0; ; n++) {
    const d = (n * Math.PI + shift) / k;
    if (d > s.length * 0.85) break;
    if (d >= s.length * 0.08) out.push(d);
  }
  return out;
}

// ---- the lighthouse ----

/** The beam's angle at time `t` (radians, turning clockwise on screen), and
 *  how bright it is there: brightest over the sea, faint inland. */
export function beamAt(
  b: Pick<BeamElement, 'period' | 'towards' | 'arc'>,
  t: number,
): { angle: number; light: number } {
  const angle = ((((t / b.period) % 1) + 1) % 1) * Math.PI * 2;
  if (b.arc !== undefined) {
    // Sea side only: full over the middle of the arc, fading out over its
    // last third, dark the rest of the way round.
    const off = Math.abs(Math.atan2(Math.sin(angle - b.towards), Math.cos(angle - b.towards)));
    const f = Math.min(1, Math.max(0, (b.arc - off) / (b.arc / 3)));
    return { angle, light: f * f * (3 - 2 * f) };
  }
  const facing = Math.cos(angle - b.towards);
  return { angle, light: Math.max(0, facing) ** 1.5 };
}

// ---- clouds ----

/** Where a cloud's middle is at time `t`, and how much of it shows (a
 *  crossing cloud fades in and out at the ends of its trip). */
export function cloudAt(c: CloudElement, t: number): { x: number; y: number; alpha: number } {
  const alpha = c.alpha ?? 1;
  if (c.cross) {
    const [ax, ay] = c.at;
    const [bx, by] = c.cross.to;
    const dist = Math.hypot(bx - ax, by - ay);
    const trip = dist / Math.max(1e-6, c.cross.speed);
    const k = (((t / trip + (c.cross.offset ?? 0)) % 1) + 1) % 1;
    const edge = Math.min(1, k / 0.05, (1 - k) / 0.05);
    return { x: ax + (bx - ax) * k, y: ay + (by - ay) * k, alpha: alpha * edge };
  }
  if (c.drift) {
    const w = wave(t, c.drift.period, c.drift.phase ?? 0);
    return { x: c.at[0] + c.drift.dx * w, y: c.at[1] + c.drift.dy * w, alpha };
  }
  return { x: c.at[0], y: c.at[1], alpha };
}

// ---- smoke and steam ----

/** One puff of smoke or steam. */
export interface Puff {
  n: number;
  x: number;
  y: number;
  /** Width, master pixels. */
  size: number;
  alpha: number;
  frame: number;
  rotation: number;
}

/**
 * The puffs in the air at time `t`. Puff n leaves the chimney at n * every
 * seconds, a little to one side, and rises, slowing; the wind pushes it
 * more the higher it gets; it grows and fades out. The same time always
 * gives the same puffs.
 */
export function puffsAt(p: PlumeElement, t: number): Puff[] {
  const out: Puff[] = [];
  if (p.every <= 0 || p.life <= 0 || p.frames.length === 0) return out;
  const first = Math.max(0, Math.ceil((t - p.life) / p.every));
  const last = Math.floor(t / p.every);
  for (let n = first; n <= last; n++) {
    const age = t - n * p.every;
    if (age < 0 || age >= p.life) continue;
    const k = age / p.life;
    const h1 = hash01(n * 13 + 1);
    const h2 = hash01(n * 13 + 2);
    const risen = p.rise * age * (1 - 0.45 * k);
    const push = 0.25 + 0.75 * k;
    out.push({
      n,
      x: p.at[0] + p.spread * (h1 - 0.5) + p.wind[0] * age * push + 2.5 * Math.sin(age * 1.3 + n),
      y: p.at[1] - risen + p.wind[1] * age * push,
      size: p.size[0] + (p.size[1] - p.size[0]) * k ** 0.6,
      alpha: p.alpha * Math.min(1, k / 0.12) * (1 - k) ** 1.4,
      frame: Math.floor(hash01(n * 13 + 3) * p.frames.length) % p.frames.length,
      rotation: (h2 - 0.5) * 0.8 + age * 0.12 * (h1 - 0.5),
    });
  }
  return out;
}

// ---- fountains and the geyser ----

/** One drop of spray. */
export interface Drop {
  n: number;
  x: number;
  y: number;
  /** Along its flight, radians (0 = east, screen y down). */
  rotation: number;
  /** Length, master pixels. */
  size: number;
  alpha: number;
}

/** A geyser's launch speed, as a share of full, at time `t`. */
export function sprayPulse(s: Pick<SprayElement, 'pulse'>, t: number): number {
  if (!s.pulse) return 1;
  const u = (((t / s.pulse.period) % 1) + 1) % 1;
  // A quick swell to full, then a slow fall back.
  const swell =
    u < 0.18 ? Math.sin((Math.PI / 2) * (u / 0.18)) : Math.cos((Math.PI / 2) * ((u - 0.18) / 0.82));
  return s.pulse.low + (1 - s.pulse.low) * Math.max(0, swell) ** 1.5;
}

/**
 * The drops in the air at time `t`. Drop n leaves the nozzle at n * every
 * seconds, at its own speed and lean, and falls under gravity until it
 * drops past the floor (the bowl). The same time gives the same drops.
 */
export function dropsAt(s: SprayElement, t: number): Drop[] {
  const out: Drop[] = [];
  if (s.every <= 0 || s.gravity <= 0) return out;
  const vMax = s.speed[1];
  const longest =
    (vMax + Math.sqrt(vMax * vMax + 2 * s.gravity * Math.max(0, s.floor))) / s.gravity;
  const first = Math.max(0, Math.ceil((t - longest) / s.every));
  const last = Math.floor(t / s.every);
  for (let n = first; n <= last; n++) {
    const born = n * s.every;
    const age = t - born;
    if (age < 0) continue;
    const v = (s.speed[0] + (s.speed[1] - s.speed[0]) * hash01(n * 17 + 1)) * sprayPulse(s, born);
    const lean = (hash01(n * 17 + 2) * 2 - 1) * s.angle;
    const vx = v * Math.sin(lean);
    const vy = -v * Math.cos(lean);
    const dy = vy * age + 0.5 * s.gravity * age * age;
    if (dy > s.floor) continue;
    const fall = vy + s.gravity * age;
    const life = (-vy + Math.sqrt(vy * vy + 2 * s.gravity * Math.max(0, s.floor))) / s.gravity;
    const k = age / Math.max(1e-6, life);
    out.push({
      n,
      x: s.at[0] + vx * age,
      y: s.at[1] + dy,
      rotation: Math.atan2(fall, vx),
      size: s.size[0] + (s.size[1] - s.size[0]) * hash01(n * 17 + 3),
      alpha: s.alpha * Math.min(1, k / 0.1, (1 - k) / 0.25),
    });
  }
  return out;
}

// ---- life in place: grazing, swishing, waving ----

/** How far a joint has turned at time `t`, as a share of its `deg` (-1 to
 *  1; 0 is the painting's own pose). */
export function jointLevel(j: Joint, t: number): number {
  const p = Math.max(1e-3, j.period);
  const u = t / p + (j.phase ?? 0);
  if (j.pattern === 'sway') return Math.sin(2 * Math.PI * u);
  const k = u - Math.floor(u);
  if (j.pattern === 'graze') {
    // Down to graze for a little over half the cycle, nibbling, then up
    // to look round. Eased both ways.
    const ease = (f: number): number => {
      const c = Math.min(1, Math.max(0, f));
      return c * c * (3 - 2 * c);
    };
    const down = Math.min(ease(k / 0.12), ease((0.62 - k) / 0.12));
    const nibble = 0.12 * Math.sin(2 * Math.PI * (t / 0.7));
    return down * (1 - 0.12 + nibble);
  }
  // A burst of swings at the start of each cycle, eased in and out.
  const swings = j.swings ?? 2;
  const swing = j.swing ?? 0.5;
  const s = k * p;
  const run = swings * swing;
  if (j.pattern === 'wave') {
    // Up over a third of a second, waving, and down again.
    const lift = 0.35;
    if (s >= run + 2 * lift) return 0;
    const f = Math.min(1, s / lift, (run + 2 * lift - s) / lift);
    const up = f * f * (3 - 2 * f);
    const w = s > lift && s < run + lift ? Math.sin(2 * Math.PI * ((s - lift) / swing)) : 0;
    return up * (0.68 + 0.32 * w);
  }
  if (s >= run) return 0;
  return Math.sin(2 * Math.PI * (s / swing)) * Math.sin(Math.PI * (s / run));
}

/** Each grid point's share of a joint's turn: 1 inside its box, fading to
 *  0 over `soft` pixels outside it. `points` holds x, y pairs. */
export function jointWeights(j: Joint, points: ArrayLike<number>): Float32Array {
  const n = Math.floor(points.length / 2);
  const out = new Float32Array(n);
  const soft = Math.max(1e-3, j.soft ?? 6);
  const [x0, y0, x1, y1] = j.box;
  for (let i = 0; i < n; i++) {
    const x = points[2 * i]!;
    const y = points[2 * i + 1]!;
    const dx = Math.max(0, x0 - x, x - x1);
    const dy = Math.max(0, y0 - y, y - y1);
    const f = Math.max(0, 1 - Math.hypot(dx, dy) / soft);
    out[i] = f * f * (3 - 2 * f);
  }
  return out;
}

/**
 * An idle piece's grid at time `t`: from its rest points (x, y pairs in the
 * frame's pixels), each joint turned about its pivot by its weight's share
 * of the turn, and the picture breathing up from its foot (`h`, the
 * frame's height). Writes into `out`; returns false when it's at rest.
 */
export function idlePose(
  e: Pick<IdleElement, 'joints' | 'breathe'>,
  t: number,
  rest: ArrayLike<number>,
  weights: readonly Float32Array[],
  h: number,
  out: Float32Array,
): boolean {
  const joints = e.joints ?? [];
  const angles = joints.map((j) => ((j.deg * Math.PI) / 180) * jointLevel(j, t));
  const swell = e.breathe
    ? e.breathe.amount *
      0.5 *
      (1 - Math.cos(2 * Math.PI * (t / e.breathe.period + (e.breathe.phase ?? 0))))
    : 0;
  const moving = swell !== 0 || angles.some((a) => Math.abs(a) > 1e-4);
  const n = Math.floor(rest.length / 2);
  for (let i = 0; i < n; i++) {
    let x = rest[2 * i]!;
    let y = rest[2 * i + 1]!;
    if (moving) {
      y = h - (h - y) * (1 + swell);
      joints.forEach((j, k) => {
        const a = angles[k]! * (weights[k]?.[i] ?? 0);
        if (a === 0) return;
        const c = Math.cos(a);
        const s = Math.sin(a);
        const dx = x - j.at[0];
        const dy = y - j.at[1];
        x = j.at[0] + dx * c - dy * s;
        y = j.at[1] + dx * s + dy * c;
      });
    }
    out[2 * i] = x;
    out[2 * i + 1] = y;
  }
  return moving;
}

/** Seconds a second pose takes to fade in or out, by default. */
export const POSE_FADE = 0.08;

/** How far a piece's second pose has faded in at time `t` (0 to 1). */
export function poseLevel(pose: NonNullable<IdleElement['pose']>, t: number): number {
  const p = Math.max(1e-3, pose.period);
  const fade = Math.max(1e-3, pose.fade ?? POSE_FADE);
  const u = t / p + (pose.phase ?? 0);
  const s = (u - Math.floor(u)) * p;
  const f = Math.min(1, s / fade, (pose.hold + 2 * fade - s) / fade);
  return Math.max(0, f);
}

/**
 * Where an idle piece's cycle of pictures is at time `t`: the picture shown
 * (`lo`: 0 is the piece's own frame, k its cycle's frame k - 1), and the next
 * one faded in over it by `mix`. It eases from the first to the last and
 * back each period, so it lingers at both ends (breathed in, breathed out).
 */
export function cycleAt(
  cycle: NonNullable<IdleElement['cycle']>,
  t: number,
): { lo: number; hi: number; mix: number } {
  const n = cycle.frames.length;
  if (n === 0) return { lo: 0, hi: 0, mix: 0 };
  const p = Math.max(1e-3, cycle.period);
  const level = n * 0.5 * (1 - Math.cos(2 * Math.PI * (t / p + (cycle.phase ?? 0))));
  const lo = Math.min(n - 1, Math.floor(level));
  return { lo, hi: lo + 1, mix: Math.min(1, level - lo) };
}

/** An idle piece's bob at time `t`, master pixels (down is positive). */
export function idleBob(e: Pick<IdleElement, 'bob'>, t: number): number {
  return e.bob ? e.bob.px * wave(t, e.bob.period, e.bob.phase ?? 0) : 0;
}

// ---- butterflies ----

/** One butterfly in flight. */
export interface Flit {
  n: number;
  x: number;
  y: number;
  /** Its heading, radians (0 = up the picture, clockwise). */
  rotation: number;
  /** How open its wings are (0 shut, 1 open). */
  open: number;
  frame: number;
}

/**
 * The butterflies at time `t`. Butterfly n roams round its spot on a
 * looping course of two waves at odd rates, so it never quite repeats;
 * it faces the way it flies, with its wings beating in short runs.
 */
export function flitsAt(e: FlitElement, t: number): Flit[] {
  const out: Flit[] = [];
  const count = Math.max(0, Math.floor(e.count));
  const p = Math.max(1, e.period);
  e.spots.forEach((spot, s) => {
    for (let i = 0; i < count; i++) {
      const n = s * 16 + i;
      const h = (k: number): number => hash01(n * 23 + k);
      const fx = (0.7 + 0.6 * h(1)) / p;
      const fy = (0.7 + 0.6 * h(2)) / p;
      const ax = 2 * Math.PI * h(3);
      const ay = 2 * Math.PI * h(4);
      const pos = (u: number): { x: number; y: number } => ({
        x:
          spot.at[0] +
          spot.r[0] *
            (0.72 * Math.sin(2 * Math.PI * fx * u + ax) +
              0.28 * Math.sin(2 * Math.PI * 2.3 * fx * u + ay)),
        y:
          spot.at[1] +
          spot.r[1] *
            (0.72 * Math.sin(2 * Math.PI * fy * u + ay) +
              0.28 * Math.sin(2 * Math.PI * 1.7 * fy * u + ax)),
      });
      const a = pos(t);
      const b = pos(t + 0.05);
      // A flutter up and down as it beats.
      const beat = t * e.flap + h(5);
      const hop = 1.6 * Math.sin(2 * Math.PI * beat);
      // Beating in runs, with short glides wings half open.
      const run = Math.sin(2 * Math.PI * (t / (1.7 + h(6)) + h(7))) > -0.55;
      const open = run ? 0.2 + 0.8 * Math.abs(Math.cos(Math.PI * beat)) : 0.75;
      out.push({
        n,
        x: a.x,
        y: a.y + (run ? hop : 0),
        rotation: Math.atan2(b.x - a.x, -(b.y - a.y)),
        open,
        frame: e.frames.length ? Math.floor(h(8) * e.frames.length) % e.frames.length : 0,
      });
    }
  });
  return out;
}

// ---- sparkles ----

/** One sparkle. */
export interface Sparkle {
  n: number;
  x: number;
  y: number;
  size: number;
  alpha: number;
  rotation: number;
}

/**
 * The sparkles at time `t`. At each point, sparkle n appears at about
 * n * every seconds (jittered), somewhere within its radius, grows to a
 * twinkle and fades as it drifts up. The same time gives the same ones.
 */
export function sparklesAt(e: SparkleElement, t: number): Sparkle[] {
  const out: Sparkle[] = [];
  if (e.every <= 0 || e.life <= 0) return out;
  e.points.forEach(([px, py, r], k) => {
    const first = Math.max(0, Math.floor((t - e.life) / e.every) - 1);
    const last = Math.floor(t / e.every) + 1;
    for (let i = first; i <= last; i++) {
      const n = k * 100003 + i;
      const h = (j: number): number => hash01(n * 19 + j);
      const born = (i + 0.8 * (h(1) - 0.5)) * e.every;
      const age = t - born;
      if (age < 0 || age >= e.life) continue;
      const f = age / e.life;
      const ang = 2 * Math.PI * h(2);
      const dist = r * Math.sqrt(h(3));
      out.push({
        n,
        x: px + Math.cos(ang) * dist,
        y: py + Math.sin(ang) * dist * 0.8 - e.rise * age,
        size: (e.size[0] + (e.size[1] - e.size[0]) * h(4)) * Math.sin(Math.PI * f) ** 0.6,
        alpha: e.alpha * Math.sin(Math.PI * f) ** 1.5,
        rotation: (h(5) - 0.5) * 0.6 + age * 0.8,
      });
    }
  });
  return out;
}

// ---- trees swaying ----

/** The wind's direction as a unit vector (east if it has none). */
export function windOf(e: Pick<SwayElement, 'wind'>): { x: number; y: number } {
  const [x, y] = e.wind;
  const d = Math.hypot(x, y);
  return d > 1e-6 ? { x: x / d, y: y / d } : { x: 1, y: 0 };
}

/**
 * How far the painting sways at a master point at time `t`, where the
 * mask is `m` (0 to 1): a shift along the wind, at most `amount` master
 * pixels. Its phase rolls across the painting along the wind, a gust's
 * crest every `wavelength` pixels (wandering, so the crests aren't straight
 * lines), and gusts swell and ease more slowly, so trees side by side move
 * out of step like a breeze passing. The sway filter's shader works the
 * same sum, pixel by pixel.
 */
export function swayShift(
  e: Pick<SwayElement, 'amount' | 'period' | 'wavelength' | 'wind'>,
  x: number,
  y: number,
  t: number,
  m = 1,
): { dx: number; dy: number } {
  const w = windOf(e);
  const s = swayLevel(e, x, y, t) * e.amount * Math.max(0, Math.min(1, m));
  return { dx: w.x * s, dy: w.y * s };
}

/** The sway repeats exactly every this many periods, so its clock can wrap
 *  there (a shader's floats lose the fine part of a long time). */
export const SWAY_REPEAT = 20;

/** The sway's level at a point, -1 to 1 (see {@link swayShift}). */
export function swayLevel(
  e: Pick<SwayElement, 'period' | 'wavelength' | 'wind'>,
  x: number,
  y: number,
  t: number,
): number {
  const w = windOf(e);
  const lambda = Math.max(1, e.wavelength);
  const p = Math.max(1e-3, e.period);
  const tau = 2 * Math.PI;
  const phase =
    (x * w.x + y * w.y) / lambda + 0.37 * Math.sin((tau * (x - 0.6 * y)) / (2.3 * lambda));
  // Gusts every four sways, a quicker flutter on top: whole numbers of
  // cycles in SWAY_REPEAT periods.
  const gust = 0.6 + 0.4 * Math.sin(tau * (t / (4 * p) - 0.31 * phase));
  return (
    gust *
    (0.8 * Math.sin(tau * (t / p - phase)) +
      0.2 * Math.sin(tau * ((2.35 * t) / p - 2.7 * phase + 0.25)))
  );
}

// ---- the ripple and the sway on slow devices ----

/**
 * Watches the frame rate while the sea ripples (or the trees sway), and
 * says when to give them up: after a short settling time, two windows in a
 * row whose
 * median frame is slower than `slowMs`. Medians, so a hitch (tiles
 * arriving) doesn't count; two windows, so one busy moment doesn't either.
 */
export class FrameWatch {
  private times: number[] = [];
  private spent = 0;
  private settle: number;
  private slowWindows = 0;
  /** The last window's median frame, milliseconds. */
  median = 0;

  constructor(
    private readonly slowMs = 22,
    private readonly windowMs = 2000,
    private readonly settleMs = 1500,
  ) {
    this.settle = settleMs;
  }

  /** Start again (after a pause, or when the sea comes back on screen). */
  reset(): void {
    this.times = [];
    this.spent = 0;
    this.settle = this.settleMs;
    this.slowWindows = 0;
  }

  /** One frame of `ms`; true once the ripple should go. */
  frame(ms: number): boolean {
    if (this.settle > 0) {
      this.settle -= ms;
      return false;
    }
    this.times.push(ms);
    this.spent += ms;
    if (this.spent < this.windowMs) return false;
    const sorted = [...this.times].sort((a, b) => a - b);
    this.median = sorted[Math.floor(sorted.length / 2)] ?? 0;
    this.times = [];
    this.spent = 0;
    this.slowWindows = this.median > this.slowMs ? this.slowWindows + 1 : 0;
    return this.slowWindows >= 2;
  }
}
